import * as React from 'react';
import { CircledCrossIcon, CircledFilledCheckIcon, EmailIcon } from '../Icons';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import VerificationCodeComponent from '../../authentication/VerificationCodeComponent';

const CODE_LENGTH = 6;

interface RenameCodeVerificationModalProperties extends RouteComponentProps {
    onResendCodeClicked: () => void;
    onCodeEntered: (code: string) => void;
    onModalClose: () => void;
    errorMessage: string | null;
    userEmail: string;
    isModalShown: boolean;
    statusCode: number;
    isButtonDisabled: boolean;
}

interface RenameCodeVerificationModalState {
    sendAgainState: SendAgainState;
    confirmEnabled: boolean;
}

enum SendAgainState {
    SendAgainReady = 'Send again', EmailSent = 'Email sent successfully'
}

const ENTER_KEY = 'Enter';
class RenameCodeVerificationModal extends React.Component<
    RenameCodeVerificationModalProperties,
    RenameCodeVerificationModalState
> {
    private static confirmBtnId = 'confirmRename';
    private verificationCode: string | null = null;
    private sendAgainTimeOutId: any = undefined;

    constructor(props: any) {
        super(props);

        this.state = {
            sendAgainState: SendAgainState.SendAgainReady,
            confirmEnabled: false
        };
        this.clearCode = this.clearCode.bind(this);
        this.verificationClearCodeCb = this.verificationClearCodeCb.bind(this);
        this.onModalClose = this.onModalClose.bind(this);
        this.onSendEmailClicked = this.onSendEmailClicked.bind(this);
        this.renderEmailSentStatus = this.renderEmailSentStatus.bind(this);
        this.renderEmailSendAgain = this.renderEmailSendAgain.bind(this);
        this.onConfirmClicked = this.onConfirmClicked.bind(this);
        this.onCodeChanged = this.onCodeChanged.bind(this);
    }

    componentDidUpdate(prevProps: Readonly<RenameCodeVerificationModalProperties>) {
        if (prevProps.isModalShown === this.props.isModalShown) {
            return;
        }
        if (this.props.isModalShown) {
            this.showModal();
        } else {
            this.hideModal();
        }
    }

    render() {
        return (
            <div
                data-backdrop="static"
                className="modal fade initModal"
                id="renameCodeVerificationModal"
                tabIndex={-1}
                role="dialog"
                aria-labelledby="renameCodeVerificationModal"
                aria-hidden="true"
            >
                {this.renderHiddenModalTriggers()}
                <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <EmailIcon className="svg__modal green" width="36px" height="36px"/>
                            <h2 className="modal-title">
                                Verification
                            </h2>
                            <button
                                type="button"
                                id="renameModalCloseBtn"
                                className="close"
                                onClick={this.onModalClose}
                                data-dismiss="modal"
                                aria-label="Close"
                            >
                                <span aria-hidden="true">
                                    <CircledCrossIcon width="20px" height="20px"/>
                                </span>
                            </button>
                        </div>
                        <div className="modal-body pb40">
                            {!this.props.errorMessage &&
                                <p>A verification code was sent to {this.props.userEmail}. Type the verification code
                                    and press 'Confirm' to continue.</p>}
                            {!!this.props.errorMessage && this.props.errorMessage}
                            <div className="float-label-control large">
                                <div className="form" data-autocomplete="off">
                                    <div className="float-label-control no__animation six-digits ml-0 mr-0">
                                        <VerificationCodeComponent
                                            setClearCode={(clearCode) => {
                                                this.verificationClearCodeCb = clearCode;
                                            }}
                                            onCodeEntered={(code: number[]) => {
                                                this.onCodeEntered(code);
                                            }}
                                            onCodeChanged={this.onCodeChanged}
                                        />
                                    </div>
                                </div>
                                <label>Verification code</label>
                                <div className="row abs">
                                    <p className="right col minitext">
                                        {this.state.sendAgainState === SendAgainState.SendAgainReady &&
                                            (this.props.errorMessage &&
                                            this.props.statusCode === 401) &&
                                            this.renderEmailSendAgain()
                                        }
                                        {this.state.sendAgainState === SendAgainState.EmailSent && this.renderEmailSentStatus()}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                disabled={this.isConfirmDisabled()}
                                onClick={this.onConfirmClicked}
                                type="button"
                                className="btn-short green launch-marker"
                                id={RenameCodeVerificationModal.confirmBtnId}
                            >Confirm
                            </button>
                            <button
                                type="button"
                                onClick={this.onModalClose}
                                className="link btn-short launch-cancel-marker"
                                data-dismiss="modal"
                            >Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    componentWillUnmount() {
        this.clearSendAgainTimeout();
        this.clearCode();
        this.unregisterListeners();

        const renameFileSystemModalCloseButton = document.getElementById('renameModalCloseBtn');
        if (renameFileSystemModalCloseButton) {
            renameFileSystemModalCloseButton.click();
        }
    }

    componentDidMount() {
        this.registerListeners();
    }

    private renderHiddenModalTriggers(): JSX.Element {
        return (
            <>
                <button
                    style={{display: 'none'}}
                    id="hiddenCloseModalButton"
                />
                <button
                    data-toggle="modal"
                    data-target="#renameCodeVerificationModal"
                    style={{display: 'none'}}
                    id="hiddenOpenModalButton"
                />
            </>
        );
    }

    private hideModal(): void {
        (window as any).$('#hiddenCloseModalButton').click();
    }

    private showModal(): void {
        (window as any).$('#hiddenOpenModalButton').click();
    }

    private onModalClose() {
        this.clearCode();
        this.clearSendAgainTimeout();
        this.setState({sendAgainState: SendAgainState.SendAgainReady});
        this.props.onModalClose();
    }

    private isConfirmDisabled(): boolean {
        const result = !this.state.confirmEnabled ||
            this.props.isButtonDisabled;
        return result;
    }

    private verificationClearCodeCb(): void {}

    private clearSendAgainTimeout(): void {
        if (this.sendAgainTimeOutId) {
            clearTimeout(this.sendAgainTimeOutId);
            this.sendAgainTimeOutId = undefined;
        }
    }

    private unregisterListeners(): void {
        document.removeEventListener('keydown', this.handleKeyDown);
    }

    private registerListeners(): void {
        document.addEventListener('keydown', this.handleKeyDown);
    }

    private handleKeyDown(event: any): void {
        if (event.key === ENTER_KEY) {
            const btn = document.getElementById('confirmRename');
            btn?.click();
        }
    }

    private onCodeChanged(code: number[]): void {
        const filledDigits = code.filter(digit => digit !== null).length;
        if (filledDigits !== CODE_LENGTH) {
            this.clearCode();
        }
    }

    private clearCode(): void {
        this.verificationCode = null;
        this.verificationClearCodeCb();
        this.setState({confirmEnabled: false});
    }

    private onCodeEntered(code: number[]): void {
        this.setState({confirmEnabled: true});
        const codeString = code.toString().split(',').join('');
        this.verificationCode = codeString;
    }

    private renderEmailSendAgain(): JSX.Element {
        return (
            <a tabIndex={-1} href="javascript: void(0);" className="nowrap" onClick={this.onSendEmailClicked}>{this.state.sendAgainState}</a>
        );
    }

    private renderEmailSentStatus(): JSX.Element {
        return (
            <span className="green">
                <CircledFilledCheckIcon className="checkmark" width="18px" height="18px"/>
                {this.state.sendAgainState}
            </span>
        );
    }

    private onConfirmClicked(): void {
        if (this.verificationCode !== null) {
            this.props.onCodeEntered(this.verificationCode);
        }
    }

    private  onSendEmailClicked(): void {
        if (this.state.sendAgainState !== SendAgainState.SendAgainReady) {
            return;
        }

        try {
            this.props.onResendCodeClicked();
            this.clearCode();
            this.setState({sendAgainState: SendAgainState.EmailSent, confirmEnabled: true});
            this.sendAgainTimeOutId = setTimeout(() => {
                this.sendAgainTimeOutId = undefined;
                this.setState({sendAgainState: SendAgainState.SendAgainReady});
            },                                   5000);
        } catch (e) {
            this.setState({confirmEnabled: false});
        }
    }
}

const RenameCodeVerificationWithRouter = withRouter(RenameCodeVerificationModal);
export { RenameCodeVerificationWithRouter as RenameCodeVerificationModal };



// WEBPACK FOOTER //
// ./src/webportal/components/common/modals/RenameCodeVerificationModal.tsx